import type { FeatureFlags } from '@/domain/configuration';

/**
 * Who gets order capture on a page load. Read by the bootstrap on every page,
 * so it stays tiny: a flag check and a clock comparison.
 *
 * The window is the widest one Hex reports. Hex narrows it to 7 days or 24
 * hours from the session events; the browser only keeps a coarse "this
 * shopper chatted recently" gate. One window for every browser: Safari's own
 * storage limit already removes the marker when nothing could be joined.
 */
export const ORDER_CAPTURE_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Whether the bootstrap owns order capture for this org. While the flag is
 * off, the Beacon runtime captures as it did before, so orgs already capturing
 * through it keep doing so until they are moved. Identity, not truthiness: the
 * V1-embed path assigns its dynamic config unvalidated.
 */
export function isOrderCaptureOnLoadEnabled(featureFlags: FeatureFlags | undefined): boolean {
  return featureFlags?.orderCaptureOnLoad === true;
}

/** Whether a marker time falls inside the capture window. A marker ahead of the clock does not count. */
export function isWithinCaptureWindow(lastEngagedAt: number | null, now: number): boolean {
  return lastEngagedAt !== null && lastEngagedAt <= now && now - lastEngagedAt <= ORDER_CAPTURE_WINDOW_MS;
}

/**
 * How recent a recorded engagement has to be to count as happening now rather
 * than arriving from replayed history. A live send records within milliseconds;
 * a replayed one carries the date it was originally sent.
 *
 * The marker holds `Math.min(Date.parse(message.date), now)` (see
 * recordShopperEngagement), so the value is a server-stamped time already capped
 * at the browser's clock. The cap removes the dangerous direction: a server
 * clock running ahead cannot make an old chat look live.
 *
 * What remains is a server clock running behind, which makes a live send look
 * older than it is. Past this threshold it reads as replayed history and the
 * order publishes unflagged -- the same row we publish today, so the failure is
 * to under-flag rather than to brand a genuine capture. A minute is far wider
 * than the round trip that stamps the date, and skew is worth taking seriously
 * here: this event stream already carries future-dated rows from client clocks.
 */
const LIVE_ENGAGEMENT_MS = 60_000;

/**
 * Whether an engagement recorded at `sentAt` is a send happening now rather
 * than a replayed message from history. Only a send happening now means the
 * engagement begins now, which is what makes an order already on the page
 * predate it.
 */
export function isLiveEngagement(sentAt: number, now: number): boolean {
  return now - sentAt <= LIVE_ENGAGEMENT_MS;
}
